// TanStack Query hooks for agent_file_versions.
// Restore reuses useSaveAgentFile under the hood, as specified in editor spec §4.3.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { AgentFileVersion } from "@/types/agent-skills";
import { AGENT_FILES_KEY, useSaveAgentFile } from "@/hooks/useAgentFiles";

// ── Query keys (plan §1.3) ────────────────────────────────────────────────────

export function agentFileVersionsKey(fileId: string) {
  return ["agent-file-versions", fileId] as const;
}

// ── Query ─────────────────────────────────────────────────────────────────────

export function useAgentFileVersions(fileId: string) {
  return useQuery({
    queryKey: agentFileVersionsKey(fileId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("agent_file_versions")
        .select("*")
        .eq("agent_file_id", fileId)
        .order("version_number", { ascending: false });

      if (error) throw new Error(error.message);

      return (data as AgentFileVersion[]) ?? [];
    },
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────────

interface RestoreVersionArgs {
  /** The live file's id. */
  fileId: string;
  /** The version whose content we want to restore. */
  version: AgentFileVersion;
  /** The current_version loaded in the editor (for optimistic locking). */
  expectedVersion: number;
  /** Optional note; defaults to "Restored from v{n}". */
  note?: string;
}

export function useRestoreVersion() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const saveFile = useSaveAgentFile();

  return useMutation({
    mutationFn: async ({ fileId, version, expectedVersion, note }: RestoreVersionArgs) => {
      return saveFile.mutateAsync({
        id: fileId,
        expectedVersion,
        frontmatter: version.frontmatter,
        body: version.body,
        note: note ?? `Restored from v${version.version_number}`,
      });
    },
    onSuccess: (_data, { fileId }) => {
      queryClient.invalidateQueries({ queryKey: agentFileVersionsKey(fileId) });
      queryClient.invalidateQueries({ queryKey: AGENT_FILES_KEY });
    },
    onError: (err) => {
      console.error("hook:useRestoreVersion", err);
      toast({
        title: "Failed to restore version",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });
}
